import React, { useEffect, useState } from "react";
import { Panel, Empty } from "../AdminUI";
import { adminService } from "../admin.service";
import { AdminPromoCode, AdminPromoCodeUsage } from "../admin-types";

function InlineEditor({ value, onChange, onSave, onCancel, min, max }: {
    value: string;
    onChange: (v: string) => void;
    onSave: () => void;
    onCancel: () => void;
    min?: number;
    max?: number;
}) {
    return (
        <div className="flex items-center" style={{ gap: "0.25rem", display: "flex", height: "2rem" }}>
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="input-text h-8 text-black"
                style={{ width: "4rem" }}
                autoFocus
            />
            <button onClick={onSave} className="action-btn solid small h-8 leading-none">Guardar</button>
            <button onClick={onCancel} className="action-btn ghost small h-8 leading-none">✕</button>
        </div>
    );
}

interface PromoCodesListProps {
    refreshKey?: number;
}

export default function PromoCodesList({ refreshKey }: PromoCodesListProps) {
    const [codes, setCodes] = useState<AdminPromoCode[]>([]);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // New code form
    const [newCode, setNewCode] = useState("");
    const [newDiscount, setNewDiscount] = useState("0");
    const [newBonusMonths, setNewBonusMonths] = useState("0");
    const [newBonusMatches, setNewBonusMatches] = useState("0");

    // Inline editing
    const [editingDiscountId, setEditingDiscountId] = useState<number | null>(null);
    const [discountDraft, setDiscountDraft] = useState("");
    const [editingMonthsId, setEditingMonthsId] = useState<number | null>(null);
    const [monthsDraft, setMonthsDraft] = useState("");
    const [editingMatchesId, setEditingMatchesId] = useState<number | null>(null);
    const [matchesDraft, setMatchesDraft] = useState("");

    const fetchCodes = async () => {
        setLoading(true);
        try {
            const data = await adminService.fetchPromoCodes();
            setCodes(data);
        } catch (error) {
            console.error("Error fetching promo codes", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, [refreshKey]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCode) return;
        try {
            await adminService.createPromoCode({
                code: newCode.toUpperCase(),
                discount_percent: parseInt(newDiscount, 10) || 0,
                bonus_months: parseInt(newBonusMonths, 10) || 0,
                bonus_matches: parseInt(newBonusMatches, 10) || 0,
            });
            setNewCode("");
            setNewDiscount("0");
            setNewBonusMonths("0");
            setNewBonusMatches("0");
            setIsCreating(false);
            fetchCodes();
        } catch (error) {
            console.error(error);
            alert("Error creando el código promocional");
        }
    };

    const handleToggleActive = async (code: AdminPromoCode) => {
        try {
            await adminService.updatePromoCode(code.id, { is_active: !code.is_active });
            fetchCodes();
        } catch (error) {
            console.error(error);
            alert("Error al actualizar el estado del código");
        }
    };

    const handleSaveDiscount = async (id: number) => {
        const value = parseInt(discountDraft, 10);
        if (isNaN(value) || value < 0 || value > 100) {
            alert("El descuento debe ser un número entre 0 y 100");
            return;
        }
        try {
            await adminService.updatePromoCode(id, { discount_percent: value });
            setEditingDiscountId(null);
            fetchCodes();
        } catch (error) {
            console.error(error);
            alert("Error al guardar el descuento");
        }
    };

    const handleSaveMonths = async (id: number) => {
        const value = parseInt(monthsDraft, 10);
        if (isNaN(value) || value < 0) {
            alert("Los meses deben ser un número positivo");
            return;
        }
        try {
            await adminService.updatePromoCode(id, { bonus_months: value });
            setEditingMonthsId(null);
            fetchCodes();
        } catch (error) {
            console.error(error);
            alert("Error al guardar los meses");
        }
    };

    const handleSaveMatches = async (id: number) => {
        const value = parseInt(matchesDraft, 10);
        if (isNaN(value) || value < 0) {
            alert("Los matches deben ser un número positivo");
            return;
        }
        try {
            await adminService.updatePromoCode(id, { bonus_matches: value });
            setEditingMatchesId(null);
            fetchCodes();
        } catch (error) {
            console.error(error);
            alert("Error al guardar los matches");
        }
    };

    return (
        <div className="col-span-12">
            <Panel
                title="Códigos Promocionales"
                right={
                    <button
                        onClick={() => setIsCreating(!isCreating)}
                        className="action-btn solid small"
                    >
                        {isCreating ? "Cancelar" : "+ Nuevo Código"}
                    </button>
                }
            >
                {isCreating && (
                    <div className="p-4 bg-slate-50 border rounded mb-4">
                        <h3 className="mb-2 font-bold">Crear Código Promocional</h3>
                        <form onSubmit={handleCreate} className="flex gap-4 items-end flex-wrap">
                            <div className="form-group">
                                <label>Código (ej. PROMO20)</label>
                                <input
                                    type="text"
                                    value={newCode}
                                    onChange={e => setNewCode(e.target.value.toUpperCase())}
                                    className="input-text"
                                    placeholder="PROMO20"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Descuento (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={newDiscount}
                                    onChange={e => setNewDiscount(e.target.value)}
                                    className="input-text w-24"
                                />
                            </div>
                            <div className="form-group">
                                <label>Meses Extra</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={newBonusMonths}
                                    onChange={e => setNewBonusMonths(e.target.value)}
                                    className="input-text w-24"
                                />
                            </div>
                            <div className="form-group">
                                <label>Matches Extra</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={newBonusMatches}
                                    onChange={e => setNewBonusMatches(e.target.value)}
                                    className="input-text w-24"
                                />
                            </div>
                            <button type="submit" className="action-btn solid">Guardar</button>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="p-4 text-center">Cargando...</div>
                ) : codes.length === 0 ? (
                    <Empty>No hay códigos promocionales.</Empty>
                ) : (
                    <div className="table-container">
                        <table className="table w-full">
                            <thead className="text-left bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Código</th>
                                    <th className="px-3 py-2 font-medium">Estado</th>
                                    <th className="px-3 py-2 font-medium">Usos</th>
                                    <th className="px-3 py-2 font-medium">Descuento (%)</th>
                                    <th className="px-3 py-2 font-medium">Meses Extra</th>
                                    <th className="px-3 py-2 font-medium">Matches Extra</th>
                                    <th className="px-3 py-2 font-medium">Creado</th>
                                    <th className="px-3 py-2 font-medium">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {codes.map(code => (
                                    <tr key={code.id}>
                                        <td className="px-3 py-2 font-bold">{code.code}</td>
                                        <td className="px-3 py-2">
                                            {code.is_active
                                                ? <span className="bool-yes">Activo</span>
                                                : <span className="bool-no">Inactivo</span>
                                            }
                                        </td>
                                        <td className="px-3 py-2">
                                            {code.use_count > 0 ? (
                                                <span
                                                    className="bool-yes cursor-pointer hover:underline"
                                                    title={code.usages.map(u => u.company_name).join(", ")}
                                                >
                                                    {code.use_count} {code.use_count === 1 ? "empresa" : "empresas"}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {editingDiscountId === code.id ? (
                                                <InlineEditor
                                                    value={discountDraft}
                                                    onChange={setDiscountDraft}
                                                    onSave={() => handleSaveDiscount(code.id)}
                                                    onCancel={() => setEditingDiscountId(null)}
                                                    min={0}
                                                    max={100}
                                                />
                                            ) : (
                                                <span
                                                    className="cursor-pointer hover:underline"
                                                    onClick={() => { setEditingDiscountId(code.id); setDiscountDraft(String(code.discount_percent)); }}
                                                    title="Click para editar"
                                                >
                                                    {code.discount_percent > 0
                                                        ? <span className="bool-yes">{code.discount_percent}%</span>
                                                        : <span className="text-slate-400">0%</span>
                                                    }
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {editingMonthsId === code.id ? (
                                                <InlineEditor
                                                    value={monthsDraft}
                                                    onChange={setMonthsDraft}
                                                    onSave={() => handleSaveMonths(code.id)}
                                                    onCancel={() => setEditingMonthsId(null)}
                                                    min={0}
                                                />
                                            ) : (
                                                <span
                                                    className="cursor-pointer hover:underline"
                                                    onClick={() => { setEditingMonthsId(code.id); setMonthsDraft(String(code.bonus_months ?? 0)); }}
                                                    title="Click para editar"
                                                >
                                                    {(code.bonus_months ?? 0) > 0
                                                        ? <span className="bool-yes">+{code.bonus_months} mes{code.bonus_months !== 1 ? "es" : ""}</span>
                                                        : <span className="text-slate-400">—</span>
                                                    }
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {editingMatchesId === code.id ? (
                                                <InlineEditor
                                                    value={matchesDraft}
                                                    onChange={setMatchesDraft}
                                                    onSave={() => handleSaveMatches(code.id)}
                                                    onCancel={() => setEditingMatchesId(null)}
                                                    min={0}
                                                />
                                            ) : (
                                                <span
                                                    className="cursor-pointer hover:underline"
                                                    onClick={() => { setEditingMatchesId(code.id); setMatchesDraft(String(code.bonus_matches ?? 0)); }}
                                                    title="Click para editar"
                                                >
                                                    {(code.bonus_matches ?? 0) > 0
                                                        ? <span className="bool-yes">+{code.bonus_matches}</span>
                                                        : <span className="text-slate-400">—</span>
                                                    }
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {code.created_at ? new Date(code.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => handleToggleActive(code)}
                                                className={`action-btn small ${code.is_active ? "outline" : "solid"}`}
                                            >
                                                {code.is_active ? "Desactivar" : "Activar"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}
